import classNames from 'classnames/bind';
import lodashSortBy from 'lodash-es/sortBy';
import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, ButtonIcon } from '@/components/shared/buttons';
import { ContextMenu } from '@/components/shared/ContextMenu';
import { Dropdown } from '@/components/shared/Dropdown';
import { FieldErrors } from '@/components/shared/form/FieldErrors';
import { TextInput } from '@/components/shared/form/inputs/TextInput';
import { Tooltip } from '@/components/shared/Tooltip';
import { Body } from '@/components/shared/typography/Body';
import { useDropdownContext } from '@/contexts/shared/DropdownContext';
import { useIsTruncated } from '@/hooks/dom';
import type { SavedFilterLabel, SavedFilterPreset } from '@/hooks/zustand/useSavedFilters';

import { SavedFiltersSaveModal } from './SavedFiltersSaveModal';
import { SavedFiltersTrigger } from './SavedFiltersTrigger';
import style from './SavedFilters.module.sass';
import type { SavedFiltersSession } from './useSavedFiltersSession';
import {
  SAVED_FILTER_NAME_MAX_LENGTH,
  type SavedFilterNameValidationError,
  validateSavedFilterName,
} from './validateSavedFilterName';

const cx = classNames.bind(style);

const formatCriterion = ({ label, values }: SavedFilterLabel) => {
  const name = label.replace(/\s*:\s*$/, '');
  return values.length ? `${name}: ${values.join(', ')}` : name;
};

const SavedFilterName = ({ name }: { name: string, }) => {
  const nameRef = useRef<HTMLSpanElement>(null);
  const isTruncated = useIsTruncated(nameRef);

  return (
    <Tooltip
      content={name}
      disabled={!isTruncated}
      delay={[400, 100]}>
      <span className={style.optionLabelWrapper}>
        <span className={style.optionLabel} ref={nameRef}>{name}</span>
      </span>
    </Tooltip>
  );
};

const OnDropdownClose = ({ onClose }: { onClose: () => void, }) => {
  const { isOpen } = useDropdownContext();
  const wasOpenRef = useRef(isOpen);

  useEffect(() => {
    if (wasOpenRef.current && !isOpen) onClose();
    wasOpenRef.current = isOpen;
  }, [isOpen, onClose]);

  return null;
};

type SavedFiltersEditMenuProps<TFilters> = {
  preset: SavedFilterPreset<TFilters>;
  isExistingName: (name: string) => boolean;
  onCancel: () => void;
  onSave: (input: { originalName: string, name: string, }) => void;
};

const SavedFiltersEditMenu = <TFilters,>({
  preset,
  isExistingName,
  onCancel,
  onSave,
}: SavedFiltersEditMenuProps<TFilters>) => {
  const { t } = useTranslation();
  const [name, setName] = useState(preset.name);
  const [shownNameError, setShownNameError] = useState<SavedFilterNameValidationError | null>(null);
  const normalizedName = name.trim();
  const nameValidationError = validateSavedFilterName({
    name: normalizedName,
    isDuplicate: Boolean(normalizedName) && isExistingName(normalizedName),
  });

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    event.stopPropagation();

    if (!normalizedName) return;

    if (nameValidationError) {
      setShownNameError(nameValidationError);

      return;
    }

    onSave({
      originalName: preset.name,
      name: normalizedName,
    });
  };

  return (
    <form
      className={style.menuPanel}
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
      onSubmit={handleSubmit}>
      <Body size='base'>{t('savedFilters:editNameModalSubtitle')}</Body>
      <TextInput
        autoFocus={true}
        aria-label={t('savedFilters:filterNameLabel')}
        value={name}
        placeholder={t('savedFilters:filterNamePlaceholder')}
        error={Boolean(shownNameError)}
        sizeVariant='small'
        onChange={(event) => {
          setName(event.currentTarget.value);
          setShownNameError(null);
        }}
      />
      {shownNameError ? (
        <FieldErrors>
          {shownNameError === 'tooLong'
            ? t('form:errors.tooLong', { count: SAVED_FILTER_NAME_MAX_LENGTH })
            : t('savedFilters:duplicateNameError')}
        </FieldErrors>
      ) : null}
      <div className={style.menuPanelActions}>
        <Button
          sizeVariant='tiny'
          variant='secondary'
          onClick={(event) => {
            event.stopPropagation();
            onCancel();
          }}>
          {t('common:cancel')}
        </Button>
        <Button
          sizeVariant='tiny'
          disabled={!normalizedName}
          type='submit'
          onClick={(event) => event.stopPropagation()}>
          {t('savedFilters:saveChangesButton')}
        </Button>
      </div>
    </form>
  );
};

type SavedFiltersProps<TFilters extends Record<string, unknown>> = {
  session: SavedFiltersSession<TFilters>;
};

/** The visual controls have no persistence or page-update contract: they consume one session. */
export const SavedFilters = <TFilters extends Record<string, unknown>>({
  session,
}: SavedFiltersProps<TFilters>) => {
  const { t } = useTranslation();
  const [editingPreset, setEditingPreset] = useState<SavedFilterPreset<TFilters> | null>(null);
  const [revealedNames, setRevealedNames] = useState<string[]>([]);
  const [openMenuName, setOpenMenuName] = useState<string | null>(null);
  const [presetPendingDeletion, setPresetPendingDeletion] = useState<string | null>(null);
  const sortedPresets = useMemo(() => lodashSortBy(
    session.presets,
    (preset) => preset.name.toLocaleLowerCase(),
  ), [session.presets]);

  useEffect(() => {
    setEditingPreset(null);
    setRevealedNames([]);
    setOpenMenuName(null);
    setPresetPendingDeletion(null);
  }, [session.scopeId]);

  const collapseRevealedPresets = useCallback(() => setRevealedNames([]), []);
  const toggleRevealedPreset = (name: string) => {
    setRevealedNames((names) => names.includes(name)
      ? names.filter((revealedName) => revealedName !== name)
      : [...names, name]);
  };

  return (
    <div className={style.shell}>
      <Dropdown
        popperClassName={style.popper}
        trigger={<SavedFiltersTrigger state={session.triggerState} />}
        position={['bottom', 'right']}>
        <OnDropdownClose onClose={collapseRevealedPresets} />
        <ul className={cx('optionsList', { hasOpenMenu: Boolean(openMenuName) })}>
          {sortedPresets.length ? sortedPresets.map((preset) => {
            const isDefaultPreset = session.defaultPreset?.name === preset.name;
            const isChosenPreset = session.chosenPresetName === preset.name;
            const isEditedPreset = session.editedPresetName === preset.name;
            const isRevealedPreset = revealedNames.includes(preset.name);
            const isMenuOpen = openMenuName === preset.name;
            const isPendingDeletion = presetPendingDeletion === preset.name;
            const isEditingPreset = editingPreset?.name === preset.name;

            return (
              <li key={preset.name}>
                <Dropdown.CloseHandler>
                  {(closeMenu) => {
                    const handleApply = () => {
                      session.applyPreset(preset.name);
                      closeMenu();
                    };

                    return (
                      <div
                        role='button'
                        tabIndex={0}
                        className={cx('option', {
                          isSelected: isChosenPreset,
                          isRevealed: isRevealedPreset,
                          isMenuOpen,
                        })}
                        onClick={handleApply}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            handleApply();
                          }
                        }}>
                        <span className={style.optionTitleLine}>
                          <SavedFilterName name={preset.name} />
                          <span className={style.optionStatuses}>
                            {isDefaultPreset ? (
                              <span className={style.statusDefault}>{t('common:default')}</span>
                            ) : null}
                            {isEditedPreset ? (
                              <span className={style.statusEdited}>{t('savedFilters:editedBadge')}</span>
                            ) : null}
                            {isChosenPreset && !isEditedPreset ? (
                              <span className={style.statusApplied}>{t('savedFilters:appliedBadge')}</span>
                            ) : null}
                          </span>
                          {isRevealedPreset ? (
                            <span
                              className={style.revealToggle}
                              onClick={(event) => event.stopPropagation()}
                              onKeyDown={(event) => event.stopPropagation()}>
                              <ButtonIcon
                                size='small'
                                iconName='EyeClosed'
                                ariaLabel={t('savedFilters:hideValuesAriaLabel', { name: preset.name })}
                                className={style.revealToggleButton}
                                onClick={() => toggleRevealedPreset(preset.name)}
                              />
                            </span>
                          ) : null}
                          <span
                            className={cx('optionActions', { isMenuOpen })}
                            onClick={(event) => event.stopPropagation()}
                            onKeyDown={(event) => event.stopPropagation()}>
                            <ContextMenu
                              portal={true}
                              ariaLabel={t('savedFilters:actionsAriaLabel', { name: preset.name })}
                              triggerClassName={style.menuTrigger}
                              triggerSize='small'
                              triggerVariant='primaryGhost'
                              onToggle={(isOpen) => {
                                setOpenMenuName(isOpen ? preset.name : null);
                                if (!isOpen) {
                                  setPresetPendingDeletion(null);
                                  setEditingPreset(null);
                                }
                              }}>
                              {isPendingDeletion ? (
                                <Dropdown.CloseHandler>
                                  {(closeConfirmationMenu) => (
                                    <div className={style.menuPanel}>
                                      <Body size='base'>
                                        {t('savedFilters:deleteConfirmation', { name: preset.name })}
                                      </Body>
                                      <div className={style.menuPanelActions}>
                                        <Button
                                          sizeVariant='tiny'
                                          variant='secondary'
                                          onClick={(event) => {
                                            event.stopPropagation();
                                            setPresetPendingDeletion(null);
                                            closeConfirmationMenu();
                                          }}>
                                          {t('common:cancel')}
                                        </Button>
                                        <Button
                                          sizeVariant='tiny'
                                          variant='danger'
                                          onClick={(event) => {
                                            event.stopPropagation();
                                            session.deletePreset(preset.name);
                                            setPresetPendingDeletion(null);
                                            closeConfirmationMenu();
                                          }}>
                                          {t('savedFilters:confirmDeleteAction')}
                                        </Button>
                                      </div>
                                    </div>
                                  )}
                                </Dropdown.CloseHandler>
                              ) : isEditingPreset ? (
                                <Dropdown.CloseHandler>
                                  {(closeEditMenu) => (
                                    <SavedFiltersEditMenu
                                      preset={preset}
                                      isExistingName={(name) => session.presets.some((candidate) => (
                                        candidate.name === name && candidate.name !== preset.name
                                      ))}
                                      onCancel={() => {
                                        setEditingPreset(null);
                                        closeEditMenu();
                                      }}
                                      onSave={(input) => {
                                        session.renamePreset(input);
                                        setEditingPreset(null);
                                        closeEditMenu();
                                      }}
                                    />
                                  )}
                                </Dropdown.CloseHandler>
                              ) : (
                                <>
                                  <Dropdown.ClickableItem
                                    closeOnClick={true}
                                    iconName={isRevealedPreset ? 'EyeClosed' : 'EyeOpen'}
                                    onClick={() => toggleRevealedPreset(preset.name)}>
                                    {isRevealedPreset
                                      ? t('savedFilters:hideValuesAction')
                                      : t('savedFilters:showValuesAction')}
                                  </Dropdown.ClickableItem>
                                  <Dropdown.ClickableItem
                                    iconName='Pen'
                                    onClick={() => setEditingPreset(preset)}>
                                    {t('savedFilters:editNameAction')}
                                  </Dropdown.ClickableItem>
                                  <Dropdown.ClickableItem
                                    closeOnClick={true}
                                    iconName={isDefaultPreset ? 'Star' : 'EmptyStar'}
                                    onClick={() => session.toggleDefaultPreset(preset.name)}>
                                    {isDefaultPreset
                                      ? t('savedFilters:unsetDefaultAction')
                                      : t('savedFilters:setDefaultAction')}
                                  </Dropdown.ClickableItem>
                                  <Dropdown.ClickableItem
                                    iconName='Trash'
                                    variant='danger'
                                    onClick={() => {
                                      setEditingPreset(null);
                                      setPresetPendingDeletion(preset.name);
                                    }}>
                                    {t('common:delete')}
                                  </Dropdown.ClickableItem>
                                </>
                              )}
                            </ContextMenu>
                          </span>
                        </span>
                        <span className={style.criteria}>
                          <span className={style.criteriaInner}>
                            {preset.labels.length ? preset.labels.map((criterion) => (
                              <span key={criterion.key} className={style.criterion}>
                                <span className={style.criterionText}>{formatCriterion(criterion)}</span>
                              </span>
                            )) : t('savedFilters:noCriteriaSaved')}
                          </span>
                        </span>
                      </div>
                    );
                  }}
                </Dropdown.CloseHandler>
              </li>
            );
          }) : <li className={style.emptyDropdownState}>{t('savedFilters:emptyDropdownState')}</li>}
        </ul>
      </Dropdown>

      <SavedFiltersSaveModal
        isOpen={session.isSaveModalOpen}
        isExistingName={(name) => session.presets.some((preset) => preset.name === name)}
        hasFilters={session.canSaveFilters}
        duplicatePresetName={session.canSaveFilters ? session.currentPresetName : undefined}
        onClose={() => session.setSaveModalOpen(false)}
        onSave={session.saveCurrent}
        onUseExistingPreset={(name) => {
          session.applyPreset(name);
          session.setSaveModalOpen(false);
        }}
        renderSummary={(className) => session.renderSummary({
          filterValues: session.filterValues,
          fallbackLabels: session.currentLabels,
          className,
        })}
      />

    </div>
  );
};
